import React, { FC } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { palette } from '../../../theme';
import type { FieldErrors } from '../../../components/form-validation';

type AthleteField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'password'
  | 'confirmPassword'
  | 'birthday'
  | 'sex'
  | 'acceptedTerms';

export interface AthleteSignUpFormProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  birthday: string;
  sex: string;
  acceptedTerms: boolean;
  fieldErrors?: FieldErrors<AthleteField>;
  isLoading: boolean;
  accentColor?: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
  onBirthdayChange: (v: string) => void;
  onSexChange: (v: string) => void;
  onAcceptedTermsChange: (v: boolean) => void;
  onSubmit: () => void;
  onBack?: () => void;
}

const AthleteSignUpForm: FC<AthleteSignUpFormProps> = ({
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
  birthday,
  sex,
  acceptedTerms,
  fieldErrors = {},
  isLoading,
  accentColor = palette.primary.main,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onBirthdayChange,
  onSexChange,
  onAcceptedTermsChange,
  onSubmit,
  onBack,
}) => {
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    '& label': {
      color: accentColor,
      fontWeight: 600,
      fontSize: 13,
    },
    '& label.Mui-focused': {
      color: accentColor,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: accentColor,
    },
  };

  return (
    <Box>
      {onBack && (
        <Link
          component="button"
          onClick={onBack}
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            fontSize: 14,
            mb: 3,
            '&:hover': { color: 'text.primary' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
          Back
        </Link>
      )}

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Let&apos;s get started!
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create your account to begin.
      </Typography>

      {Object.keys(fieldErrors).length > 0 && (
        <Box
          sx={{
            backgroundColor: palette.error.light,
            border: `1px solid ${palette.error.main}`,
            borderRadius: 2,
            px: 2,
            py: 1,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="error">
            Please fix the highlighted fields below
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Row 1: First / Last name */}
        <Grid item xs={6}>
          <TextField
            id="signup-field-firstName"
            label="First Name"
            placeholder="Jane"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.firstName}
            helperText={fieldErrors.firstName || ''}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="signup-field-lastName"
            label="Last Name"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.lastName}
            helperText={fieldErrors.lastName || ''}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 2: Email */}
        <Grid item xs={12}>
          <TextField
            id="signup-field-email"
            label="Email"
            placeholder="you@example.com"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.email}
            helperText={fieldErrors.email || ''}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 3: Password / Confirm */}
        <Grid item xs={6}>
          <TextField
            id="signup-field-password"
            label="Password"
            placeholder="Choose a password"
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.password}
            helperText={fieldErrors.password || ''}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="signup-field-confirmPassword"
            label="Confirm Password"
            placeholder="Re-enter password"
            type="password"
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword || ''}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 4: DOB / Sex */}
        <Grid item xs={6}>
          <TextField
            id="signup-field-birthday"
            label="Date of Birth"
            type="date"
            value={birthday}
            onChange={(e) => onBirthdayChange(e.target.value)}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            error={!!fieldErrors.birthday}
            helperText={fieldErrors.birthday || ''}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small" required error={!!fieldErrors.sex} sx={fieldSx}>
            <InputLabel>Sex</InputLabel>
            <Select
              label="Sex"
              value={sex}
              onChange={(e) => onSexChange(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="" disabled>
                Select Sex
              </MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </Select>
            {fieldErrors.sex && <FormHelperText>{fieldErrors.sex}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Terms */}
        <Grid item xs={12}>
          <FormControl error={!!fieldErrors.acceptedTerms} sx={{ display: 'block' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => onAcceptedTermsChange(e.target.checked)}
                  size="small"
                  sx={{
                    color: palette.grey[300],
                    '&.Mui-checked': { color: accentColor },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  I accept the{' '}
                  <Link
                    href="https://www.coelab.co/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: accentColor, fontWeight: 600 }}
                  >
                    Terms and Conditions
                  </Link>{' '}
                  <Typography component="span" color="error" variant="body2">
                    *
                  </Typography>
                </Typography>
              }
            />
            {fieldErrors.acceptedTerms && <FormHelperText>{fieldErrors.acceptedTerms}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            disabled={isLoading}
            onClick={onSubmit}
            sx={{
              borderRadius: 999,
              py: 1.25,
              fontWeight: 600,
              fontSize: 15,
              backgroundColor: accentColor,
              '&:hover': { backgroundColor: accentColor, filter: 'brightness(0.9)' },
              '&:disabled': { backgroundColor: palette.grey[200] },
            }}
          >
            Create Account
          </Button>
        </Grid>

        {/* Sign in link */}
        <Grid item xs={12}>
          <Typography variant="body2" align="center" color="text.secondary">
            Already have an account?{' '}
            <Link href="/login" sx={{ color: accentColor, fontWeight: 700 }}>
              Sign In
            </Link>
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AthleteSignUpForm;
